import {
  createTheme,
  type MantineColorsTuple,
  type MantineTheme,
  type MantineThemeOverride,
} from "@mantine/core";

/**
 * Brand palette — a confident indigo/violet (Tailwind indigo scale).
 * Used for primary actions, active nav state, focus rings, brand accents.
 * Full 10-shade scale ([0..9]) so Mantine can derive every variant.
 */
const brand: MantineColorsTuple = [
  "#eef2ff",
  "#e0e7ff",
  "#c7d2fe",
  "#a5b4fc",
  "#818cf8",
  "#6366f1",
  "#4f46e5",
  "#4338ca",
  "#3730a3",
  "#312e81",
];

/**
 * Neutral accent used for "tech / compute" surfaces — subtle teal-cyan.
 * Reserved for resource-graph accents (CPU/RAM bars) so they don't fight
 * the brand color. Keep usage rare.
 */
const accent: MantineColorsTuple = [
  "#ecfeff",
  "#cffafe",
  "#a5f3fc",
  "#67e8f9",
  "#22d3ee",
  "#06b6d4",
  "#0891b2",
  "#0e7490",
  "#155e75",
  "#164e63",
];

const fontFamily =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Helvetica Neue", sans-serif';
const fontFamilyMonospace =
  'ui-monospace, "JetBrains Mono", SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

/**
 * NOTE: Mantine v7 removed `theme.colorScheme` from the theme object —
 * color scheme is now runtime state accessed via `useMantineColorScheme()`.
 * Per-component dark/light styling inside the theme is therefore done via
 * CSS variables (`var(--mantine-color-dark-7)` etc.) rather than JS branches.
 * These variables resolve correctly at runtime because Mantine rewrites
 * them per active color scheme.
 */
export const theme: MantineThemeOverride = createTheme({
  primaryColor: "brand",
  primaryShade: { light: 6, dark: 5 },
  white: "#ffffff",
  black: "#0b0c0f",
  fontFamily,
  fontFamilyMonospace,
  headings: {
    fontFamily,
    fontWeight: "600",
    sizes: {
      h1: { fontSize: "1.875rem", lineHeight: "2.25rem" },
      h2: { fontSize: "1.375rem", lineHeight: "1.75rem" },
      h3: { fontSize: "1.125rem", lineHeight: "1.5rem" },
    },
  },
  defaultRadius: "md",
  cursorType: "pointer",
  colors: {
    brand,
    accent,
  },
  autoContrast: true,
  luminanceThreshold: 0.4,
  components: {
    Button: {
      defaultProps: {
        size: "sm",
      },
      styles: {
        root: {
          fontWeight: 500,
          transition:
            "background-color 150ms ease, color 150ms ease, border-color 150ms ease, transform 80ms ease",
        },
      },
    },
    ActionIcon: {
      defaultProps: { size: "lg" },
    },
    Card: {
      defaultProps: {
        radius: "md",
        padding: "lg",
        withBorder: true,
      },
      styles: {
        root: {
          background: "var(--mantine-color-body)",
          borderColor: "var(--mantine-color-default-border)",
        },
      },
    },
    Table: {
      styles: (t: MantineTheme) => ({
        th: {
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: t.fontSizes.xs,
          fontWeight: 600,
          color: "var(--mantine-color-dimmed)",
        },
      }),
    },
    TextInput: {
      defaultProps: { size: "sm" },
    },
    PasswordInput: {
      defaultProps: { size: "sm" },
    },
    Select: {
      defaultProps: { size: "sm" },
    },
    NumberInput: {
      defaultProps: { size: "sm" },
    },
    Badge: {
      styles: {
        root: { fontWeight: 600, letterSpacing: "0.01em" },
      },
    },
    Modal: {
      defaultProps: { centered: true, radius: "lg" },
    },
  },
});
